import React, { useCallback, useContext, useState, useEffect } from "react";
import SearchBar from "../molecules/SearchBar";
import { search, sendCustomEvent } from "../../common/utility";
import ResponsiveTable from "../organisms/ResponsiveTable";
import { DocumentLink } from "../molecules/DocumentLink";
import trackService, { AUDITEVENTTYPE } from "services/trackService";
import { PDFViewerContext } from "contexts/PdfViewerContext";
import { FEATURE_FLAGS } from "../../common/constants";

import { DocumentHeader } from "types/documents/DocumentHeader";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { filter, firstValueFrom, map, tap } from "rxjs";
import { plans$ } from "services/subjects";
import { policies$ } from "services/subjects/policies";
import planService from "services/planService";
import documentService from "services/documentService";
import { useNotifyError } from "hooks/useNotifyError";
import { useTrackPageEffect } from "hooks/services/useTrackPageEffect";
import { AuditEvent } from "types/track/AuditEventType";

const SERVICE_URL = "api/members/documents";

export const Benefits = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState("");
  const { addAndOpenDocument } = useContext(PDFViewerContext);
  const { data: policiesResponse, error: policiesError } = useQuery({
    queryKey: ["policies"],
    queryFn: async () => firstValueFrom(policies$),
  });
  useNotifyError(policiesError, "Error loading policies");
  const {
    data: documents,
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => {
      const observable$ = documentService.get$.pipe(
        filter((response) => !!response?.response),
        map((response) => response.response.results)
      );

      return await firstValueFrom(observable$);
    },
  });
  useNotifyError(documentsError, "Error loading documents");
  const {
    data: plans,
    isLoading: isLoadingPlans,
    error: plansError,
  } = useQuery({
    queryKey: ["plans"],
    queryFn: async () =>
      firstValueFrom(
        planService.get$().pipe(
          map((response) => response.response.results),
          tap((plans) => plans$.next(plans))
        )
      ),
  });
  useNotifyError(plansError, "Error loading plans");
  useTrackPageEffect({ eventType: AuditEvent.MemberPortalViewBenefits });
  const handleDocumentClick = useCallback(
    (document: DocumentHeader) => {
      const customEventType =
        AUDITEVENTTYPE[
          document.download_custom_event_name as keyof typeof AUDITEVENTTYPE
        ];
      customEventType && sendCustomEvent({ eventType: customEventType });
      addAndOpenDocument({
        serviceUrl: SERVICE_URL,
        documentPath: document.id,
        displayName: document.filename || "",
      });
    },
    [addAndOpenDocument]
  );

  function handleSearch(searchTerm: string): void {
    setSearchTerm(searchTerm);
  }

  return (
    <Stack>
      <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
        Your plan - {policiesResponse?.policies?.[0].policy_name} - Benefit
        Highlights
      </Typography>
      <Typography variant="h3" component="h2" gutterBottom>
        Documents
      </Typography>
      <Stack mb={2}>
        {isLoadingDocuments ? (
          <Stack spacing={1} data-testid="loading-documents">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton variant="rounded" width="100%" height={20} key={i} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1} data-testid="documents">
            {(documents || [])
              .filter(
                (document) =>
                  document &&
                  (!document.data || document.data.document_type !== "eob")
              )
              .map((document, i) => (
                <DocumentLink
                  key={i}
                  document={document}
                  onDocumentClick={handleDocumentClick}
                />
              ))}
          </Stack>
        )}
      </Stack>
      <Box mb={2}>
        <SearchBar placeholder="Find keywords" onChange={handleSearch} />
      </Box>
      <Box mb={2}>
        <ResponsiveTable
          emptyComponent={
            !!searchTerm && (
              <Typography>
                No results found for{" "}
                <Typography color="#0376a8" component="span">
                  {searchTerm}
                </Typography>
              </Typography>
            )
          }
          isFirstTimeLoading={isLoadingPlans}
          tableOptions={{
            data: search(plans || [], searchTerm),
            columns: [
              {
                header: "Services Covered",
                accessorKey: "benefit_category_display_text",
                cell: ({ cell }) => <strong>{cell.getValue()}</strong>,
                meta: {
                  width: "20%",
                  hideHeaderOnMobile: true,
                },
                enableSorting: true,
              },
              {
                header: "What You Pay",
                accessorKey: "benefit_details_text",
                enableSorting: true,
                cell: ({ cell }) => <Box sx={{
                  whiteSpace: 'pre-line',
                }}>{cell.getValue()}</Box>,
              },
              {
                header: "More Info",
                accessorKey: "benefit_display_text",
                meta: {
                  width: "50%",
                },
                enableSorting: true,
                cell: ({ cell }) => <Box  sx={{
                  whiteSpace: 'pre-line',
               } }>{cell.getValue()}</Box>,
              },
            ],
          }}
        />
      </Box>
    </Stack>
  );
};

export default Benefits;
